import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiErrorDetail, reportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canManage, isCleaner } from "@/lib/perms";
import { PropertyForm } from "@/pages/Properties";
import { StatusBadge, SeverityBadge } from "@/components/Badges";
import { CONDITION_LABELS, money } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BriefingText } from "@/components/BriefingText";
import DocumentVault from "@/components/DocumentVault";
import BookingSync from "@/components/BookingSync";
import { ArrowLeft, Pencil, Sparkles, Loader2, BedDouble, Bath, KeyRound, User, Trash2, FileText, Mail } from "lucide-react";
import IssueDialog from "@/components/IssueDialog";
import { DirectionsButton } from "@/components/DirectionsButton";
import { WeatherPanel } from "@/components/WeatherPanel";
import { WeatherBriefing } from "@/components/WeatherBriefing";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prop, setProp] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [issues, setIssues] = useState([]);
  const [owners, setOwners] = useState([]);
  const [edit, setEdit] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [hero, setHero] = useState(0);
  const [inspections, setInspections] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    api.get(`/properties/${id}`).then(({ data }) => setProp(data));
    api.get("/jobs", { params: { property_id: id } }).then(({ data }) => setJobs(data));
    api.get("/issues", { params: { property_id: id } }).then(({ data }) => setIssues(data));
    if (canManage(user)) {
      api.get(`/insights/property/${id}/summary`).then(({ data }) => setSummary(data));
      api.get("/inspections", { params: { property_id: id } }).then(({ data }) => setInspections(data)).catch(() => {});
      api.get(`/properties/${id}/condition-history`).then(({ data }) => setHistory(data)).catch(() => {});
      api.get("/invoices", { params: { property_id: id } }).then(({ data }) => setInvoices(data)).catch(() => {});
    }
  }, [id, user]);

  useEffect(() => {
    load();
    if (canManage(user)) api.get("/users", { params: { role: "owner" } }).then(({ data }) => setOwners(data));
  }, [load, user]);

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post(`/insights/property/${id}/summary`);
      setSummary(data);
      toast.success("Condition briefing generated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate summary");
    }
    setGenBusy(false);
  };

  const emailToOwner = async () => {
    setMailBusy(true);
    try {
      const { data } = await api.post(`/insights/property/${id}/email`);
      toast.success(`Report sent to ${data.to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send the report");
    }
    setMailBusy(false);
  };

  if (!prop) return <Loader2 className="h-6 w-6 animate-spin text-royal" />;

  const photos = prop.photos || [];

  return (
    <div className="space-y-6" data-testid="property-detail-page">
      <Link to="/properties" data-testid="back-to-properties" className="inline-flex items-center gap-1.5 text-sm text-royal hover:text-gold transition-colors duration-200">
        <ArrowLeft className="h-4 w-4" /> All properties
      </Link>

      <div className="rounded-lg overflow-hidden border border-border bg-white">
        <div className="h-[220px] sm:h-[320px] lg:h-[380px] bg-secondary relative">
          {photos[hero] ? (
            <img src={fileUrl(photos[hero])} alt={prop.name} className="h-full w-full object-cover" />
          ) : (
            <div className="h-full w-full flex items-center justify-center text-sm text-muted-foreground">No photos uploaded</div>
          )}
          <div className="absolute top-3 right-3 z-10">
            <DirectionsButton property={prop} />
          </div>
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-royal-deep/95 via-royal-deep/70 to-transparent p-4 sm:p-6">
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-gold font-semibold">
              {CONDITION_LABELS[prop.condition] || prop.condition}
            </span>
            <h1 className="font-display text-xl sm:text-3xl lg:text-4xl font-bold text-white mt-1 leading-tight break-words">{prop.name}</h1>
            <p className="text-blue-100/80 text-xs sm:text-sm mt-1 break-words">{prop.address}{prop.city ? `, ${prop.city}` : ""} {prop.state}</p>
          </div>
        </div>
        {photos.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto" data-testid="photo-thumbnails">
            {photos.map((p, i) => (
              <button
                key={p}
                data-testid={`thumb-${i}`}
                onClick={() => setHero(i)}
                className={`h-16 w-20 shrink-0 rounded-md overflow-hidden border-2 transition-colors duration-200 ${i === hero ? "border-gold" : "border-transparent"}`}
              >
                <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 sm:gap-3">
        {canManage(user) && (
          <Button data-testid="edit-property-btn" onClick={() => setEdit(true)} className="bg-royal hover:bg-royal-deep text-white gap-2">
            <Pencil className="h-4 w-4" /> Edit property
          </Button>
        )}
        {<IssueDialog propertyId={id} onCreated={load} />}
        {canManage(user) && (
          <Button
            data-testid="download-report-btn"
            variant="outline"
            onClick={() => window.open(reportUrl(id), "_blank")}
            className="border-royal/30 text-royal hover:bg-royal/5 gap-2"
          >
            <FileText className="h-4 w-4" /> Monthly report (PDF)
          </Button>
        )}
        {canManage(user) && (
          <Button
            data-testid="delete-property-btn"
            variant="outline"
            onClick={() => setConfirmDelete(true)}
            className="border-destructive/40 text-destructive hover:bg-destructive/5 gap-2 sm:ml-auto"
          >
            <Trash2 className="h-4 w-4" /> Delete property
          </Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="space-y-6 min-w-0">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="property-facts">
            {[
              { icon: BedDouble, k: "Bedrooms", v: prop.bedrooms },
              { icon: Bath, k: "Bathrooms", v: prop.bathrooms },
              { icon: User, k: "Owner", v: prop.owner_name || "—" },
              { icon: KeyRound, k: "Access", v: prop.access_notes || "—" },
            ].map((f) => (
              <div key={f.k} className="rounded-lg border border-border bg-white p-4">
                <f.icon className="h-4 w-4 text-gold" />
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-2">{f.k}</p>
                <p className="text-sm font-semibold text-royal-deep mt-0.5 break-words">{f.v}</p>
              </div>
            ))}
          </div>

          <section className="rounded-lg border border-border bg-white p-4 sm:p-5" data-testid="property-details-section">
            <div className="flex items-center gap-3">
              <h2 className="font-display text-lg font-semibold text-royal-deep">Property Details</h2>
              <div className="h-px flex-1 gold-rule" />
            </div>
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              {[
                ["Property name", prop.name],
                ["Full address", `${prop.address}${prop.city ? `, ${prop.city}` : ""} ${prop.state || ""}`],
                ["Bedrooms / Bathrooms", `${prop.bedrooms} bd · ${prop.bathrooms} ba`],
                ["Condition", CONDITION_LABELS[prop.condition] || prop.condition],
                ["Owner", prop.owner_name || "No owner assigned"],
                ["Access / lockbox", prop.access_notes || "—"],
                ["WiFi", prop.wifi || "—"],
                ["Open issues", String(issues.filter((i) => i.status === "open").length)],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</dt>
                  <dd className="text-royal-deep font-medium mt-0.5 break-words">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Care notes</p>
              <p className="text-sm text-royal-deep mt-1 leading-relaxed">{prop.notes || "—"}</p>
            </div>
          </section>

          <Tabs defaultValue="jobs">
            <TabsList data-testid="detail-tabs">
              <TabsTrigger value="jobs" data-testid="tab-jobs">Work ({jobs.length})</TabsTrigger>
              <TabsTrigger value="issues" data-testid="tab-issues">Issues ({issues.length})</TabsTrigger>
              <TabsTrigger value="inspections" data-testid="tab-inspections">Condition ({inspections.length})</TabsTrigger>
              {canManage(user) && (
                <TabsTrigger value="money" data-testid="tab-money">Money ({invoices.length})</TabsTrigger>
              )}
              <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
              {canManage(user) && (
                <TabsTrigger value="calendar" data-testid="tab-calendar">Calendar</TabsTrigger>
              )}
              <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
            </TabsList>

            <TabsContent value="jobs" className="mt-4">
              <div className="rounded-lg border border-border bg-white divide-y divide-border">
                {jobs.length === 0 && <p className="p-5 text-sm text-muted-foreground">No jobs for this property yet.</p>}
                {jobs.map((j) => (
                  <Link key={j._id} to={`/jobs/${j._id}`} data-testid={`property-job-${j._id}`} className="flex items-center gap-4 px-5 py-3.5 hover:bg-secondary/60 transition-colors duration-200">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-royal-deep">{j.title}</p>
                      <p className="text-xs text-muted-foreground">{j.scheduled_date} · {j.scheduled_time} · {j.cleaner_name || "Unassigned"}</p>
                    </div>
                    <StatusBadge value={j.status} />
                  </Link>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="issues" className="mt-4">
              <div className="rounded-lg border border-border bg-white divide-y divide-border">
                {issues.length === 0 && <p className="p-5 text-sm text-muted-foreground">No issues reported.</p>}
                {issues.map((i) => (
                  <div key={i._id} className="px-5 py-4" data-testid={`property-issue-${i._id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-semibold text-royal-deep">{i.title}</p>
                      <div className="flex gap-2">
                        <SeverityBadge value={i.severity} />
                        <StatusBadge value={i.status} />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">{i.description}</p>
                    <p className="text-[11px] text-muted-foreground mt-2">Reported by {i.reported_by_name}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="inspections" className="mt-5 space-y-3" data-testid="property-inspections-pane">
              {history && (
                <div className="grid grid-cols-3 gap-3">
                  {[["Latest score", history.latest_score ?? "—"], ["Average", history.average_score ?? "—"],
                    ["Inspections", history.inspections]].map(([k, v]) => (
                    <div key={k} className="rounded-md border border-border p-3 text-center">
                      <p className="font-display text-2xl font-bold text-royal-deep">{v}</p>
                      <p className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{k}</p>
                    </div>
                  ))}
                </div>
              )}
              {(history?.recurring_problems || []).length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="property-recurring-problems">
                  <p className="text-sm font-semibold text-amber-900">Recurring problems</p>
                  {history.recurring_problems.map((r) => (
                    <p key={r.area} className="text-xs text-amber-900 mt-1">
                      {r.label} — flagged {r.occurrences} times
                    </p>
                  ))}
                </div>
              )}
              {inspections.length === 0 && (
                <p className="text-sm text-muted-foreground">No inspections recorded for this property yet.</p>
              )}
              {inspections.map((i) => (
                <div key={i._id} className="flex items-center gap-3 rounded-md border border-border p-3"
                     data-testid={`property-inspection-${i._id}`}>
                  <p className="font-display text-lg font-bold text-royal-deep">{i.overall_score ?? "—"}</p>
                  <div className="min-w-0">
                    <p className="text-sm text-royal-deep">{i.inspector_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(i.submitted_at || i.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="ml-auto text-[11px] font-semibold">
                    {i.passed ? <span className="text-emerald-700">PASSED</span> : <span className="text-red-700">FAILED</span>}
                  </span>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="money" className="mt-5 space-y-3" data-testid="property-money-pane">
              {invoices.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No invoices for this property yet. Auto-draft them from the Invoices page.
                </p>
              ) : (
                invoices.map((inv) => (
                  <Link key={inv._id} to="/invoices" data-testid={`property-invoice-${inv._id}`}
                        className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3 hover:border-royal/40 transition-colors duration-200">
                    <p className="font-display font-semibold text-royal-deep">{inv.number}</p>
                    <StatusBadge value={inv.status} />
                    <p className="text-xs text-muted-foreground">{inv.period_start} – {inv.period_end}</p>
                    <p className="ml-auto font-semibold text-royal-deep">{money(inv.total)}</p>
                  </Link>
                ))
              )}
            </TabsContent>
            <TabsContent value="documents" className="mt-5" data-testid="property-documents-pane">
              <DocumentVault entityType="property" entityId={id} canEdit={canManage(user)} />
            </TabsContent>

            {canManage(user) && (
              <TabsContent value="calendar" className="mt-5" data-testid="property-calendar-pane">
                <BookingSync propertyId={id} icalUrl={prop.ical_url} syncedAt={prop.ical_synced_at}
                             syncError={prop.ical_sync_error} attemptedAt={prop.ical_sync_attempted_at}
                             canEdit={canManage(user)} onSynced={load} />
              </TabsContent>
            )}
          </Tabs>
        </div>

        {canManage(user) && (
        <div className="space-y-6 h-fit min-w-0">
        <WeatherPanel propertyId={id} />
        <WeatherBriefing propertyId={id} ownerName={prop.owner_name} ownerEmail={prop.owner_email}
                         canManage={canManage(user)} />
        <div className="ai-card p-4 sm:p-5 min-w-0 overflow-hidden" data-testid="ai-summary-card">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-gold" />
            <h3 className="font-display text-lg font-semibold text-royal-deep">Owner condition briefing</h3>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            AI-generated from job history, checklists and reported issues.
          </p>
          {summary ? (
            <BriefingText text={summary.summary} testId="ai-summary-text" />
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">No briefing generated yet.</p>
          )}
          {summary?.generated_at && (
            <p className="mt-3 text-[11px] text-muted-foreground">
              Generated {new Date(summary.generated_at).toLocaleString()}
            </p>
          )}
          {canManage(user) && (
            <Button
              data-testid="generate-summary-btn"
              onClick={generate}
              disabled={genBusy}
              className="mt-4 w-full bg-gold hover:bg-gold/90 text-royal-deep font-semibold"
            >
              {genBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : summary ? "Regenerate briefing" : "Generate briefing"}
            </Button>
          )}
          {canManage(user) && summary && (
            <>
              <Button
                data-testid="email-briefing-btn"
                onClick={emailToOwner}
                disabled={mailBusy || !prop.owner_email}
                variant="outline"
                className="mt-2.5 w-full border-royal/30 text-royal-deep hover:bg-royal/5 font-semibold"
              >
                {mailBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" /> Email this to the owner
                  </>
                )}
              </Button>
              <p className="mt-2 text-[11px] text-muted-foreground" data-testid="briefing-recipient">
                {prop.owner_email
                  ? `Goes to ${prop.owner_name || "the owner"} at ${prop.owner_email}`
                  : "Assign an owner with an email address to send this"}
              </p>
            </>
          )}
        </div>
        </div>
        )}
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid="delete-property-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-royal-deep">Delete {prop.name}?</DialogTitle>
            <DialogDescription>
              This permanently removes the property along with all of its jobs and issues. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              data-testid="cancel-delete-property-btn"
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              className="border-royal/30 text-royal hover:bg-royal/5"
            >
              Keep property
            </Button>
            <Button
              data-testid="confirm-delete-property-btn"
              disabled={deleting}
              onClick={async () => {
                setDeleting(true);
                try {
                  await api.delete(`/properties/${id}`);
                  toast.success("Property deleted");
                  navigate("/properties");
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete property");
                  setDeleting(false);
                }
              }}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={edit} onOpenChange={setEdit}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-royal-deep">Edit property</DialogTitle></DialogHeader>
          <PropertyForm initial={prop} owners={owners} onSaved={load} onClose={() => setEdit(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
